import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, CircularProgress, IconButton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, Paper,
    Dialog, DialogTitle, DialogContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { resolucionesExpedienteJWT, eliminarResolucionJWT } from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';
import { capitalizar } from '../../utils/capitalizar';
import ConfirmDialog from '../../components/Shared/ConfirmDialog';
import NuevaResolucionDialog from './NuevaResolucionDialog';

function buildPdfUrl(idExpediente, idResolucion) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const base = nodeApiPath(`expedientes/${idExpediente}/resoluciones/${idResolucion}/download`);
    return token ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : base;
}

function ResolucionesTab({ idExpediente }) {
    const [resoluciones, setResoluciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Preview PDF
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    // Confirm delete
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const resp = await resolucionesExpedienteJWT(idExpediente);
            if (resp?.ok) setResoluciones(resp.resoluciones || []);
        } catch { }
        finally { setLoading(false); }
    }, [idExpediente]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleVerPdf = (resolucion) => {
        const url = buildPdfUrl(idExpediente, resolucion.Id);
        setPreviewUrl(url);
        setPreviewTitle(resolucion.Nombre_Archivo || `Resolución Nº ${resolucion.Numero_Anio}`);
        setPreviewOpen(true);
    };

    const handlePedirEliminar = (resolucion) => {
        setRowToDelete(resolucion);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!rowToDelete) return;
        try {
            const resp = await eliminarResolucionJWT(idExpediente, rowToDelete.Id);
            if (resp?.ok) cargar();
        } catch { }
        setRowToDelete(null);
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                    {resoluciones.length} resolución{resoluciones.length !== 1 ? 'es' : ''} registrada{resoluciones.length !== 1 ? 's' : ''}
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: '4px' }}
                >
                    Nueva Resolución
                </Button>
            </Box>

            {resoluciones.length === 0 ? (
                <Box textAlign="center" py={4}>
                    <Typography variant="body1" sx={{ color: '#bbb' }}>
                        No hay resoluciones registradas para este expediente
                    </Typography>
                </Box>
            ) : (
                <Paper variant="outlined" sx={{ borderRadius: '4px', overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Nº Resolución</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Nº Expediente</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Origen</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Monto</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">PDF</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center" width={50}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {resoluciones.map(r => (
                                <TableRow
                                    key={r.Id}
                                    hover
                                    onClick={() => r.URL_Archivo && handleVerPdf(r)}
                                    sx={{ cursor: r.URL_Archivo ? 'pointer' : 'default' }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {r.Numero_Anio}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{r.Nro_Expediente || '-'}</TableCell>
                                    <TableCell>{r.Fecha}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.Descripcion}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {r.ReparticionNombre ? capitalizar(r.ReparticionNombre) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {r.Monto ? `$ ${Number(r.Monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {r.URL_Archivo ? (
                                            <Tooltip title={r.Nombre_Archivo || 'Ver PDF'}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleVerPdf(r); }} color="error">
                                                    <PictureAsPdfIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePedirEliminar(r); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {/* Dialog nueva resolución */}
            <NuevaResolucionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                idExpediente={idExpediente}
                onCreada={cargar}
            />

            {/* Preview PDF en iframe */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{ style: { width: '95vw', maxWidth: '95vw', borderRadius: 4 } }}
            >
                <DialogTitle style={{ background: '#5d7b9d', padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontWeight: 700 }}>{previewTitle || 'Resolución PDF'}</span>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => window.open(previewUrl, '_blank', 'noopener')}
                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { borderColor: '#fff' } }}
                    >
                        Abrir en pestaña nueva
                    </Button>
                </DialogTitle>
                <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ height: '82vh', minHeight: 420 }}>
                        <iframe
                            src={previewUrl}
                            title={previewTitle || 'PDF'}
                            style={{ width: '100%', height: '100%', border: 0 }}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirm eliminar */}
            <ConfirmDialog
                open={confirmOpen}
                onClose={() => { setConfirmOpen(false); setRowToDelete(null); }}
                onConfirm={handleConfirmDelete}
                title="¿Eliminar resolución?"
                message={`¿Está seguro de eliminar la resolución Nº ${rowToDelete?.Numero_Anio || ''}? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
                confirmColor="secondary"
            />
        </Box>
    );
}

export default ResolucionesTab;
